import { rankOf, suitOf } from "./cards";
import { evaluate7 } from "./evaluator";
import { estimateEquity } from "./equity";

// Pro+ AI: range-based, position- and stack-aware. Sees only its own cards, the board and public actions.
const rnd = Math.random;
const check = { type: "check" };
const passive = (v) => (v.toCall === 0 ? check : { type: "call" });
const fold = (v) => (v.toCall === 0 ? check : { type: "fold" });
const allIn = (v) => (v.canRaise ? { type: "raise", amount: v.maxTo } : passive(v));
function raiseTo(v, amt) {
  if (!v.canRaise) return passive(v);
  let to = Math.max(v.minRaiseTo, Math.round(amt / v.sbUnit) * v.sbUnit);
  if (to >= v.maxTo * 0.7) to = v.maxTo;
  return { type: "raise", amount: Math.min(to, v.maxTo) };
}

// Percentile of every starting hand (0 = best). Built once from heads-up equity + playability.
let PCT52 = null;
function pctTable() {
  if (PCT52) return PCT52;
  const classes = [];
  for (let r1 = 14; r1 >= 2; r1--) {
    for (let r2 = r1; r2 >= 2; r2--) {
      const variants = r1 === r2 ? [false] : [true, false];
      variants.forEach((suited) => {
        const c1 = (r1 - 2) * 4, c2 = (r2 - 2) * 4 + (suited ? 0 : 1);
        const gap = r1 - r2 - 1;
        let score = estimateEquity([c1, c2], [], 1, 500);
        if (suited) score += 0.025;
        if (r1 !== r2 && gap === 0) score += 0.02;
        if (r1 !== r2 && gap === 1) score += 0.01;
        if (suited && r1 === 14) score += 0.01;
        classes.push({ r1, r2, suited, pair: r1 === r2, score, combos: r1 === r2 ? 6 : suited ? 4 : 12 });
      });
    }
  }
  classes.sort((a, b) => b.score - a.score);
  const byKey = {};
  let cum = 0;
  classes.forEach((c) => { cum += c.combos; byKey[`${c.r1}-${c.r2}-${c.suited ? 1 : 0}`] = cum / 1326; });
  PCT52 = new Float32Array(52 * 52);
  for (let a = 0; a < 52; a++) {
    for (let b = 0; b < 52; b++) {
      if (a === b) continue;
      const r1 = Math.max(rankOf(a), rankOf(b)), r2 = Math.min(rankOf(a), rankOf(b));
      const suited = r1 !== r2 && suitOf(a) === suitOf(b);
      PCT52[a * 52 + b] = byKey[`${r1}-${r2}-${suited ? 1 : 0}`];
    }
  }
  return PCT52;
}
export const handPct = (h) => pctTable()[h[0] * 52 + h[1]];

// Equity vs opponents' assumed public ranges (rejection-sampled), not vs their actual cards.
export function rangedEquity(hole, board, ranges, iters) {
  const T = pctTable();
  const taken = new Uint8Array(52);
  const pick = () => { let c; do { c = Math.floor(rnd() * 52); } while (taken[c]); taken[c] = 1; return c; };
  let total = 0;
  for (let it = 0; it < iters; it++) {
    taken.fill(0);
    hole.forEach((c) => { taken[c] = 1; });
    board.forEach((c) => { taken[c] = 1; });
    const opp = ranges.map((r) => {
      for (let t = 0; t < 30; t++) {
        const a = pick(), b = pick();
        if (T[a * 52 + b] <= r || t === 29) return [a, b];
        taken[a] = 0; taken[b] = 0;
      }
      return null;
    });
    const b = board.slice();
    while (b.length < 5) b.push(pick());
    const mine = evaluate7(hole.concat(b));
    let ties = 0, lost = false;
    for (const o of opp) {
      const sc = evaluate7([o[0], o[1], ...b]);
      if (sc > mine) { lost = true; break; }
      if (sc === mine) ties++;
    }
    if (!lost) total += 1 / (ties + 1);
  }
  return total / iters;
}

export function texture(board) {
  const sc = [0, 0, 0, 0];
  board.forEach((c) => sc[suitOf(c)]++);
  const maxSuit = Math.max(...sc);
  const ranks = [...new Set(board.map(rankOf))].sort((a, b) => a - b);
  let wet = maxSuit >= 3 ? 0.7 : maxSuit === 2 ? 0.35 : 0;
  for (let i = 0; i + 2 < ranks.length; i++) if (ranks[i + 2] - ranks[i] <= 4) { wet += 0.4; break; }
  if (ranks.length < board.length) wet -= 0.2;
  return Math.max(0, Math.min(1, wet));
}

export function drawInfo(hole, board) {
  const all = hole.concat(board);
  const sc = [0, 0, 0, 0];
  all.forEach((c) => sc[suitOf(c)]++);
  const flush = sc.some((n, s) => n === 4 && hole.some((c) => suitOf(c) === s));
  let mask = 0;
  all.forEach((c) => { mask |= 1 << rankOf(c); if (rankOf(c) === 14) mask |= 2; });
  const holeMask = hole.reduce((m, c) => m | (1 << rankOf(c)), 0);
  let oesd = false;
  for (let lo = 2; lo <= 10; lo++) {
    const w = 0b1111 << lo;
    if ((mask & w) === w && (holeMask & w) && !(mask & (1 << (lo - 1))) && !(mask & (1 << (lo + 4)))) oesd = true;
  }
  return { flush, oesd };
}

const OPEN = { EP: 0.13, MP: 0.18, CO: 0.26, BTN: 0.42, SB: 0.38, BB: 0 };
const PUSH12 = { EP: 0.11, MP: 0.14, CO: 0.19, BTN: 0.27, SB: 0.36, BB: 0.3 };

function preflop(v) {
  const hp = handPct(v.hole);
  const [a, b] = v.hole;
  const r1 = Math.max(rankOf(a), rankOf(b)), r2 = Math.min(rankOf(a), rankOf(b));
  const suited = suitOf(a) === suitOf(b);
  const bluffy = (suited && r1 === 14 && r2 <= 5) || (suited && r1 - r2 <= 2 && r2 >= 5) || (r1 === 14 && r2 >= 9) || (suited && r1 === 13 && r2 >= 9);
  const hu = v.tableSize === 2;
  const eff = v.effBB;
  const odds = v.toCall / (v.pot + v.toCall);
  const late = v.pos === "BTN" || v.pos === "CO";
  const rr = v.raiserRange ?? 0.3;

  if (eff <= 12) {
    if (v.raiseCount === 0) {
      const base = hu ? (v.pos === "SB" ? 0.6 : 0.45) : PUSH12[v.pos];
      const pushPct = Math.min(1, base * (12 / Math.max(eff, 1.5)) ** 0.85);
      if (v.toCall === 0) return hp <= pushPct * 0.6 ? allIn(v) : check;
      return hp <= pushPct ? allIn(v) : fold(v);
    }
    if (hp <= Math.max(0.02, rr * 0.15)) return allIn(v);
    const behind = Math.max(0, v.nOpp - 1);
    const eq = rangedEquity(v.hole, [], [rr], 400);
    return eq > odds + 0.02 + behind * 0.04 ? allIn(v) : fold(v);
  }

  if (v.raiseCount >= 1 && v.toCall >= v.stack * 0.4) {
    if (hp <= 0.02) return allIn(v);
    const behind = Math.max(0, v.nOpp - 1);
    const eq = rangedEquity(v.hole, [], [rr], 400);
    return eq > odds + 0.02 + behind * 0.04 ? allIn(v) : fold(v);
  }

  const openBB = eff > 40 ? (v.pos === "SB" ? 3 : late ? 2.3 : 2.5) : eff > 20 ? 2.2 : 2;

  if (v.raiseCount === 0) {
    if (v.toCall === 0) {
      if (hp <= 0.12 || (v.limpers > 0 && bluffy && rnd() < 0.25)) return raiseTo(v, v.bb * (3.5 + v.limpers));
      return check;
    }
    let pct = hu ? (v.pos === "SB" ? 0.8 : 0) : OPEN[v.pos];
    if (v.limpers > 0) pct *= 0.7;
    if (hp <= pct) {
      if (eff <= 18 && hp > pct * 0.6 && (r1 === r2 || r1 === 14)) return allIn(v);
      return raiseTo(v, v.bb * (openBB + v.limpers));
    }
    if (v.pos === "SB" && !hu && v.limpers === 0 && hp <= 0.6 && rnd() < 0.25) return passive(v);
    if (v.limpers > 0 && v.pos === "BTN" && hp <= 0.45 && (suited || r1 === r2)) return passive(v);
    return fold(v);
  }

  const big = v.currentBet / v.bb > 4.5;
  if (v.raiseCount === 1) {
    let valuePct = Math.max(0.025, rr * 0.22) * (big ? 0.85 : 1);
    let callPct;
    if (v.pos === "BB") callPct = Math.min(0.65, rr * (1.0 + (0.4 - odds) * 2.5));
    else if (v.pos === "SB") callPct = rr * 0.35;
    else callPct = rr * (late ? 0.7 : 0.45);
    if (big) callPct *= 0.7;
    const jam3 = eff <= 25;
    if (jam3) { callPct *= 0.6; valuePct *= 1.4; }
    const threeBet = () => (jam3 ? allIn(v) : raiseTo(v, v.currentBet * (late ? 3 : 3.8)));
    if (hp <= valuePct) return late && !jam3 && rnd() < 0.2 ? passive(v) : threeBet();
    if (hp > callPct && hp <= callPct + 0.12 && bluffy && rnd() < (late || v.pos === "BB" || v.pos === "SB" ? 0.4 : 0.2)) return threeBet();
    if (hp <= callPct) return hp <= valuePct * 1.8 && rnd() < 0.25 ? threeBet() : passive(v);
    return fold(v);
  }

  if (v.raiseCount === 2) {
    const value4 = Math.max(0.025, rr * 0.3);
    let callPct = rr * (v.inPosition ? 0.9 : 0.6) * (eff > 50 ? 1 : 0.6);
    if (odds < 0.3) callPct *= 1.3;
    if (hp <= value4) return eff <= 45 || rnd() < 0.4 ? allIn(v) : raiseTo(v, v.currentBet * 2.3);
    if (suited && r1 === 14 && r2 <= 5 && eff > 45 && rnd() < 0.35) return raiseTo(v, v.currentBet * 2.3);
    return hp <= callPct ? passive(v) : fold(v);
  }

  const jam = Math.max(0.02, rr * 0.6);
  if (hp <= jam) return allIn(v);
  if (hp <= jam * 1.6 && odds < 0.35) return passive(v);
  return fold(v);
}

function postflop(v) {
  const eq = rangedEquity(v.hole, v.board, v.opponents.map((o) => o.range), 700);
  const tex = texture(v.board);
  const { flush, oesd } = drawInfo(v.hole, v.board);
  const river = v.street === "river";
  const hasDraw = !river && (flush || oesd);
  const hu = v.nOpp === 1;
  const multi = 1 / Math.max(1, v.nOpp);
  const odds = v.toCall / (v.pot + v.toCall);
  const spr = v.stack / Math.max(1, v.pot);
  const size = tex > 0.5 ? 0.66 + rnd() * 0.1 : 0.33 + rnd() * 0.1;
  const bet = (f) => raiseTo(v, v.pot * f);

  if (v.toCall === 0) {
    if (eq > 0.72) {
      if (!river && rnd() < (v.inPosition ? 0.12 : 0.25) * (tex < 0.4 ? 1.3 : 0.6)) return check;
      if (river) return bet(rnd() < 0.3 ? 1.25 : 0.8);
      return bet(size + (spr < 2 ? 0.3 : 0));
    }
    if (eq > 0.5) {
      if (river) return v.inPosition && rnd() < 0.3 ? bet(0.33) : check;
      if ((v.wasAggressor || v.inPosition) && rnd() < 0.5 * multi + 0.1) return bet(0.33);
      return check;
    }
    if (hasDraw) return rnd() < (v.wasAggressor ? 0.65 : 0.4) * (hu ? 1 : 0.5) ? bet(Math.max(size, 0.5)) : check;
    if (river) return hu && (v.wasAggressor || v.inPosition) && eq < 0.25 && rnd() < 0.3 ? bet(rnd() < 0.3 ? 1.25 : 0.8) : check;
    if (v.wasAggressor && hu && v.street === "flop" && rnd() < (tex < 0.4 ? 0.65 : 0.4)) return bet(0.33);
    if (v.inPosition && hu && rnd() < 0.2) return bet(0.5);
    return check;
  }

  const betRatio = Math.min(1.5, v.toCall / Math.max(1, v.pot - v.toCall));
  const raised = v.raiseCount >= 2;
  const adj = eq - 0.03 * betRatio - (raised ? 0.06 : 0);
  const need = odds - (v.inPosition ? 0.04 : 0.02) - (river ? 0.02 : 0);
  const commit = v.toCall >= v.stack * 0.45;
  const raiseBig = () => (commit || spr < 1.5 ? allIn(v) : raiseTo(v, v.currentBet + (v.pot + v.toCall) * 0.8));

  if (adj > 0.78 && v.canRaise) {
    if (!raised && !river && tex < 0.5 && rnd() < 0.3) return passive(v);
    return raiseBig();
  }
  if (hasDraw && hu && !raised && v.canRaise && !commit && rnd() < 0.25) return raiseBig();
  if (adj > need) return passive(v);
  if (hasDraw && eq + 0.05 > odds) return passive(v);
  if (v.inPosition && hu && v.street === "flop" && betRatio <= 0.6 && !raised && rnd() < 0.3) return passive(v);
  if (river && hu && !raised && eq < 0.15 && v.canRaise && rnd() < 0.06) return raiseTo(v, v.currentBet * 2.5);
  return fold(v);
}

export function proPlusDecide(view) {
  return view.street === "preflop" ? preflop(view) : postflop(view);
}
